import type { Canvas } from 'fabric';
import addShirt from '@/utils/addShirt';
import addItem from '@/utils/addItem';
import { getItemTypes, type IconRef } from '@/config/itemTypes';
import { isDugout } from '@/config/features';

const copyItem = (
  canvas: Canvas,
  iconType: string,
  isMobile: boolean,
  scaleFactor: number,
  // The resolved theme. copyItem runs from an Editor event handler and so has no context of its own;
  // components/Editor/Editor.tsx threads it through from ToolContext.
  theme: string,
): void => {
  const { shirtTypes, goalTypes, coneTypes, itemTypes, toolTypes } =
    getItemTypes(theme);
  const activeObjs = canvas.getActiveObjects();

  if (!activeObjs) return; // (we can only copy something if something is actually selected)

  for (const activeObj of activeObjs) {
    const variation = activeObj.variation;
    if (variation === 'lineControl') continue; // lines/lineControls are not able to be copied
    const copyOffset = 20;
    const top = (activeObj.top ?? 0) + copyOffset;
    const left = (activeObj.left ?? 0) + copyOffset;

    const spreadArr = [
      ...shirtTypes,
      ...goalTypes,
      ...itemTypes,
      ...coneTypes,
      ...toolTypes,
    ];
    // Dugout repeats `variation` across teams (jouw team + extra team both `shirt-attack`), so it
    // resolves the source item by its unique `name`; the other themes have unique variations.
    const found = isDugout(theme)
      ? spreadArr.find((i) => i.name === activeObj.name)
      : spreadArr.find((i) => i.variation === variation);
    if (!found) continue;

    // `iconType` ('defaultIcon'/'alternateIcon') selects which icon of the set to clone.
    const src = (found as unknown as Record<string, IconRef | undefined>)[
      iconType
    ]?.src;
    const name = found.name;
    const width = isMobile ? found.width : found.width;
    const height = isMobile ? found.height : found.height;
    const defaultIcon = found.defaultIcon;
    const alternateIcon = found.alternateIcon;
    const color = found.color;
    const fontColor = found.fontColor;

    if (variation?.includes('shirt')) {
      addShirt({
        name: name ?? '',
        left,
        top,
        variation,
        color,
        canvas,
        src: src ?? '',
        width,
        height,
        defaultIcon,
        alternateIcon,
        iconType,
        fontColor,
        scaleFactor,
        theme,
      });
    } else {
      addItem({
        name: name ?? '',
        left,
        top,
        variation,
        color,
        canvas,
        src: src ?? '',
        width,
        height,
        defaultIcon,
        alternateIcon,
        iconType,
        scaleFactor,
        theme,
      });
    }
  }
};

export default copyItem;
